import axios, {
  AxiosHeaders,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios'

import { env } from '@/config/env'
import { isAccessTokenExpired } from '@/lib/jwt'
import { tokenStorage } from '@/lib/token-storage'

import { endpoints } from './endpoints'
import { ApiError, ApiErrorCode, toApiError } from './api-error'

interface RetryableConfig extends InternalAxiosRequestConfig {
  /** Set once a request has already been replayed after a token refresh. */
  _retried?: boolean
  /** Opt a request out of the Authorization header and the refresh dance. */
  skipAuth?: boolean
}

export interface RequestConfig extends AxiosRequestConfig {
  skipAuth?: boolean
}

const baseHeaders = { 'Content-Type': 'application/json' }

/**
 * How an array query param goes out: `branchIds=a&branchIds=b`.
 *
 * Axios defaults to the bracketed form, `branchIds[]=a`. The API is mounted
 * behind Express's SIMPLE query parser, which does not strip those brackets —
 * the param arrives under the literal name `branchIds[]`, which no DTO has, so
 * the filter is silently dropped and the list comes back unnarrowed.
 *
 * Set on the clients rather than per request. Four call sites were already
 * carrying their own copy of this, and the next list filter to take several
 * values would have been a fifth — one that works until somebody picks a second
 * value, which is not what gets tested by hand.
 */
const paramsSerializer = { indexes: null } as const

/** Interceptor-free client, used for the refresh call so it cannot recurse. */
const bareClient = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: env.apiTimeout,
  headers: baseHeaders,
  paramsSerializer,
})

/* -------------------------------------------------------------------------- */
/* Token renewal                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Single-flight refresh: while one refresh is in progress every other caller
 * waits on the same promise instead of stampeding the endpoint.
 *
 * This is the *only* place the app may spend a refresh token. The service
 * rotates on every call — each response carries a new refresh token and revokes
 * the one presented — so a second, parallel refresh path would present a token
 * the first call had already burned. Persisting the rotated token immediately
 * is what lets a session outlive its first reload.
 */
let refreshInFlight: Promise<string> | null = null

interface RefreshBody {
  accessToken: string
  /** Present when the service rotates; absent on services that do not. */
  refreshToken?: string
}

async function requestAccessToken(): Promise<string> {
  const refreshToken = tokenStorage.getRefreshToken()
  if (!refreshToken) throw toApiError(new Error('No refresh token available.'))

  const { data } = await bareClient.post<RefreshBody>(endpoints.auth.refresh, { refreshToken })

  if (!data?.accessToken) throw toApiError(new Error('Refresh returned no access token.'))

  // Store the rotation before the access token: if the write throws, the next
  // boot retries with a token we know the server still honours.
  if (data.refreshToken && data.refreshToken !== refreshToken) {
    tokenStorage.setRefreshToken(data.refreshToken)
  }

  tokenStorage.setAccessToken(data.accessToken)
  return data.accessToken
}

export function refreshAccessToken(): Promise<string> {
  refreshInFlight ??= requestAccessToken().finally(() => {
    refreshInFlight = null
  })

  return refreshInFlight
}

/* -------------------------------------------------------------------------- */
/* Clients                                                                    */
/* -------------------------------------------------------------------------- */

async function attachAccessToken(config: RetryableConfig): Promise<RetryableConfig> {
  if (config.skipAuth) return config

  let accessToken = tokenStorage.getAccessToken()

  // Renew up front when the JWT is already spent — saves a round trip we know
  // would be rejected. A failure here falls through on purpose: the response
  // interceptor remains the authority on a dead session.
  if (isAccessTokenExpired(accessToken) && tokenStorage.getRefreshToken()) {
    try {
      accessToken = await refreshAccessToken()
    } catch {
      accessToken = null
    }
  }

  if (accessToken) {
    const headers = AxiosHeaders.from(config.headers)
    headers.set('Authorization', `Bearer ${accessToken}`)
    config.headers = headers
  }

  return config
}

/**
 * Every host takes the same bearer token, so they share one refresh cycle: a
 * rejection from any of them replays through the same single-flight renewal.
 *
 * `ownsSession` marks the client whose rejections are allowed to *end* the
 * session. Only the auth service can say that a token is dead; the main API
 * answers a bad request with HTTP 400 "No user signed in." rather than a 401,
 * and it has its own reasons to reject a caller, so its verdict must not be
 * mistaken for our token expiring.
 */
function createClient(baseURL: string, ownsSession: boolean): AxiosInstance {
  const client = axios.create({
    baseURL,
    timeout: env.apiTimeout,
    headers: baseHeaders,
    paramsSerializer,
  })

  client.interceptors.request.use(attachAccessToken)

  client.interceptors.response.use(
    (response) => response,
    async (error: unknown) => {
      if (!axios.isAxiosError(error) || !error.config) throw toApiError(error)

      const config = error.config as RetryableConfig
      const apiError = toApiError(error)
      const isRefreshCall = config.url === endpoints.auth.refresh

      if (!apiError.isSessionExpired || isRefreshCall || config.skipAuth || config._retried) {
        throw apiError
      }

      // Renew only when staleness is a plausible explanation. A token whose own
      // `exp` says it is still good was not rejected for being expired, so
      // replaying it would fail again — and each attempt spends a rotation.
      // An unreadable expiry stays eligible: then this is the only renewal path.
      if (!isAccessTokenExpired(tokenStorage.getAccessToken(), 0)) {
        throw apiError
      }

      if (!tokenStorage.getRefreshToken()) {
        if (ownsSession) {
          tokenStorage.clear()
          tokenStorage.emitSessionExpired()
        }
        throw apiError
      }

      config._retried = true

      try {
        await refreshAccessToken()
      } catch {
        // The refresh token itself was rejected — that verdict is the auth
        // service's regardless of which client surfaced it.
        tokenStorage.clear()
        tokenStorage.emitSessionExpired()
        throw apiError
      }

      return client.request(config)
    },
  )

  return client
}

/** Auth micro-service: login, logout, refresh, change-password. */
export const http = createClient(env.apiBaseUrl, true)

/** Main NexQr API: everything else, including the profile. */
export const mainHttp = createClient(env.mainApiBaseUrl, false)

/**
 * Inventory service: stock items, locations and stock levels.
 *
 * Not the session's owner either. Its 401 and 403 are about this service — an
 * account it cannot provision, a token that names no tenant — not proof that the
 * token died, so they must never sign the merchant out.
 */
export const inventoryHttp = createClient(env.inventoryApiBaseUrl, false)

/* With no host configured, a relative request would land on this app's own
   server, which answers every path with index.html: a 200 that reads as an
   empty list. Refused here instead, with a reason a screen can show. */
if (!env.inventoryApiBaseUrl) {
  inventoryHttp.interceptors.request.use(() =>
    Promise.reject(
      new ApiError('Inventory is not available in this environment yet.', {
        status: 0,
        code: ApiErrorCode.NotConfigured,
      }),
    ),
  )
}

/* -------------------------------------------------------------------------- */
/* Typed helpers — unwrap `data`, always reject with an `ApiError`             */
/* -------------------------------------------------------------------------- */

function createApi(client: AxiosInstance) {
  return {
    async get<T>(url: string, config?: RequestConfig): Promise<T> {
      try {
        const { data } = await client.get<T>(url, config)
        return data
      } catch (error) {
        throw toApiError(error)
      }
    },

    async post<T>(url: string, body?: unknown, config?: RequestConfig): Promise<T> {
      try {
        const { data } = await client.post<T>(url, body, config)
        return data
      } catch (error) {
        throw toApiError(error)
      }
    },

    async put<T>(url: string, body?: unknown, config?: RequestConfig): Promise<T> {
      try {
        const { data } = await client.put<T>(url, body, config)
        return data
      } catch (error) {
        throw toApiError(error)
      }
    },

    async patch<T>(url: string, body?: unknown, config?: RequestConfig): Promise<T> {
      try {
        const { data } = await client.patch<T>(url, body, config)
        return data
      } catch (error) {
        throw toApiError(error)
      }
    },

    async delete<T>(url: string, config?: RequestConfig): Promise<T> {
      try {
        const { data } = await client.delete<T>(url, config)
        return data
      } catch (error) {
        throw toApiError(error)
      }
    },
  }
}

export const api = createApi(http)
export const mainApi = createApi(mainHttp)
export const inventoryApi = createApi(inventoryHttp)
